import { RadioButton } from '@pmi/dsm-react';
import { useState } from 'react';
import { SavedPayment } from '../../models';
import { useGetSavedPaymentQuery } from '../../apiSlice';
import styles from '../enhancedEnrollmentModal.module.scss';

// Map brand names to exact SVG filenames in /public/payment-icons/icon/
const PAYMENT_ICON_MAPPING: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  americanexpress: 'AMEX',
  diners: 'DinersClub',
  dinersclub: 'DinersClub',
  discover: 'Discover',
  jcb: 'JCB',
  alipay: 'AliPay',
  paypal: 'PayPal',
  netbanking: 'NetBanking',
  upi: 'UPI',
  // shortcuts for common variations
  mc: 'Mastercard',
  amex: 'AMEX',
};

const getPaymentIconPath = (brand: string): string => {
  const normalizedBrand = brand.toLowerCase().trim();
  const iconName =
    PAYMENT_ICON_MAPPING[normalizedBrand] ||
    `${brand.charAt(0).toUpperCase()}${brand.slice(1)}`;
  return `/payment-icons/icon/${iconName}.svg`;
};

interface SavedPaymentMethodSelectorProps {
  onPaymentChange: (payment: SavedPayment | 'new' | null) => void;
  onPaymentMethodSelected: () => void;
  onShowAddNewPaymentChange: (show: boolean) => void;
}

export default function SavedPaymentMethodSelector({
  onPaymentChange,
  onPaymentMethodSelected,
  onShowAddNewPaymentChange,
}: SavedPaymentMethodSelectorProps) {
  const { data: savedPaymentData } = useGetSavedPaymentQuery();
  const [selectedPayment, setSelectedPayment] = useState<SavedPayment | null>(
    null,
  );
  const [showAddNewPayment, setShowAddNewPayment] = useState(false);

  const handleSavedPaymentChange = (payment: SavedPayment) => {
    setSelectedPayment(payment);
    setShowAddNewPayment(false);
    onPaymentChange(payment);
    onShowAddNewPaymentChange(false);
    onPaymentMethodSelected();
  };
  const handleAddNewPaymentChange = () => {
    setShowAddNewPayment(true);
    setSelectedPayment(null);
    onPaymentChange('new');
    onShowAddNewPaymentChange(true);
    onPaymentMethodSelected();
  };

  return (
    <div>
      <div className={`${styles.ccSectionLabel} mb-2`}>
        Saved Payment Options
      </div>
      {(savedPaymentData as SavedPayment[]).map((payment) => (
        <div
          key={payment.tokenId}
          className={`border border-1 rounded-3 mb-3 ${styles.paymentCard}`}
          style={
            selectedPayment?.tokenId === payment.tokenId
              ? {
                  backgroundColor: '#f5f5f5',
                }
              : {}
          }
        >
          <RadioButton
            id={payment.tokenId}
            name="payment-method"
            value={payment.tokenId}
            checked={selectedPayment?.tokenId === payment.tokenId}
            onChange={() => {
              handleSavedPaymentChange(payment);
            }}
            label={
              <div className="d-flex align-items-center gap-2">
                <img
                  src={getPaymentIconPath(payment.brand)}
                  alt=""
                  style={{ height: '24px' }}
                />
                <span>•••• {payment.cardLast4Number}</span>
              </div>
            }
          />
        </div>
      ))}

      <div className={`${styles.ccSectionLabel} mb-2`}>
        Additional payment options
      </div>

      <div
        className={`border border-1 rounded-3 mb-3 ${styles.paymentCard} ${styles.newPaymentCard}`}
      >
        <RadioButton
          id="add-new-payment"
          name="payment-method"
          value="new"
          checked={showAddNewPayment}
          onChange={handleAddNewPaymentChange}
          label={
            <div className="d-flex align-items-center justify-content-between">
              <span>Credit or debit card</span>
              <div className={`d-flex ${styles.paymentIconsContainer}`}>
                {Object.entries(PAYMENT_ICON_MAPPING)
                  .slice(0, 3)
                  .map(([brand, name]) => (
                    <img
                      key={brand}
                      src={getPaymentIconPath(brand)}
                      alt={name}
                      style={{ height: '24px' }}
                    />
                  ))}
                <span className={`${styles.ccSectionLabel} ms-1 fw-normal`}>
                  +4
                </span>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
